<template>
  <component
    :is="clickableCard ? Link : 'div'"
    :href="clickableCard ? profileHref : undefined"
    :class="rootClasses"
    :aria-label="clickableCard ? profileAriaLabel : undefined"
  >
    <div :class="innerClasses">
      <div v-if="specialist.specialisations?.length" class="flex flex-wrap items-start gap-[8px]">
        <SpecialisationBadge
          v-for="spec in specialist.specialisations"
          :key="spec.id"
          :name="spec.name"
          :site-context="spec.site_context"
        />
      </div>

      <div class="flex w-full items-start gap-[12px]">
        <div v-if="photoUrl" class="size-[96px] shrink-0">
          <img
            :alt="displayName"
            class="size-full object-cover rounded-t-md pointer-events-none"
            :src="photoUrl"
            draggable="false"
          >
        </div>

        <div class="flex min-w-0 flex-1 flex-col items-start justify-center">
          <div class="flex w-full flex-col items-start">
            <p
              v-if="specialist.title"
              class="font-sans text-[16px] leading-[24px] text-gray"
            >
              {{ specialist.title }}
            </p>
            <p
              class="font-sans font-semibold tracking-[-0.4px] text-dark"
              :class="nameSizeClass"
            >
              {{ displayName }}
            </p>
          </div>

          <component
            :is="clickableCard ? 'span' : Link"
            :href="clickableCard ? undefined : profileHref"
            class="flex h-[40px] items-center justify-center gap-[8px] rounded-[8px] px-[16px] py-[10px] transition-colors"
            :class="clickableCard
              ? 'text-family-clinic group-hover:bg-family-clinic-secondary'
              : 'hover:bg-family-clinic-secondary'"
            :aria-hidden="clickableCard ? true : undefined"
          >
            <span class="whitespace-nowrap font-sans text-[14px] font-medium leading-[16px] text-code-medical-center-nav">
              {{ t('common.more') }}
            </span>
            <svg
              class="size-[20px] shrink-0 text-code-medical-center-nav"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </component>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import SpecialisationBadge from '@/Shared/SpecialisationBadge.vue';
import { useTranslations } from '@/utils/translations.js';

const { t } = useTranslations();

const props = defineProps({
  specialist: {
    type: Object,
    required: true,
  },
  specialistsBaseUrl: {
    type: String,
    default: '/specjalisci',
  },
  // When true the card renders flat (no rounded background/shadow and no inner
  // horizontal padding) so it can sit inside another container separated only
  // by a divider line.
  flat: {
    type: Boolean,
    default: false,
  },
  // When true the whole card navigates to the profile (carousels, header menu).
  // When false only the "more" button is a link (embedded lists on detail pages).
  clickableCard: {
    type: Boolean,
    default: false,
  },
});

const displayName = computed(() => props.specialist.fullName || props.specialist.full_name);

const nameSizeClass = computed(() => {
  const length = displayName.value?.length ?? 0;
  if (length > 24) return 'text-[16px] leading-[24px]';
  if (length > 18) return 'text-[18px] leading-[26px]';
  return 'text-[20px] leading-[28px]';
});
const photoUrl = computed(() => props.specialist.photo || props.specialist.photo_url);
const profileHref = computed(() => `${props.specialistsBaseUrl}/${props.specialist.slug}`);

const profileAriaLabel = computed(() => (
  displayName.value
    ? `${displayName.value} — ${t('common.more')}`
    : t('common.more')
));

const rootClasses = computed(() => {
  const alignment = props.clickableCard
    ? 'min-h-[148px] flex-col justify-end'
    : 'items-end';

  const base = props.flat
    ? `group flex w-full ${alignment}`
    : `group flex w-full ${alignment} overflow-hidden rounded-[12px] bg-white shadow-[0px_18px_20px_-17px_rgba(13,0,12,0.05),0px_11px_13px_-8px_rgba(16,24,40,0.1)]`;

  if (!props.clickableCard) return base;

  return [
    base,
    'cursor-pointer no-underline transition-colors hover:bg-neutral-50',
  ];
});

const innerClasses = computed(() => {
  if (props.flat) return 'flex w-full flex-col gap-[12px]';

  const base = 'flex w-full flex-col gap-[12px] px-[24px] pt-[12px]';
  return props.clickableCard ? `${base} flex-1 justify-end` : base;
});
</script>
